import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, Bell, BellRing, X } from 'lucide-react';
import ChoreBoard, { todayYmd } from './ChoreBoard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['januari','februari','mars','april','maj','juni','juli','augusti','september','oktober','november','december'];
const HABIT_DAY_LABELS = [1, 5, 10, 15, 20, 25, 31];

// Accumulating debounce: merges multiple patches from different fields into
// a single save so rapid multi-field edits are never dropped.
function useDebouncedSave(save, delay = 700) {
  const pending = useRef({});
  const timer = useRef(null);
  return useCallback((partial) => {
    pending.current = { ...pending.current, ...partial };
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const toSend = pending.current;
      pending.current = {};
      timer.current = null;
      save(toSend);
    }, delay);
  }, [save, delay]);
}

const MON_SHORT = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];

function RowDateChip({ parsed, meta, text, open, onOpen, onMeta, idx }) {
  if (!(text || '').trim()) return null;
  const m = meta || { in_paper: true, date: '', time: '' };
  const label = parsed?.date ? `${Number(parsed.date.slice(8))} ${MON_SHORT[Number(parsed.date.slice(5, 7)) - 1]}${parsed.time ? ` ${parsed.time}` : ''}` : 'datum?';
  return (
    <span className="relative flex items-center gap-0.5 flex-shrink-0">
      <button onClick={onOpen} title={parsed?.date ? 'Hittat datum, klicka för att ändra' : 'Inget datum hittat, klicka för att välja'}
        className={`text-[9px] font-bold rounded-full px-1.5 py-0.5 ${parsed?.date ? 'bg-[#E8DCF5] text-[#4E4370]' : 'bg-[#FFF1E0] text-[#B07A10]'}`} data-testid={`monthly-important-date-${idx}`}>
        📅 {label}
      </button>
      <button onClick={() => onMeta({ in_paper: !m.in_paper })} title={m.in_paper ? 'Med i dagstidningen' : 'Inte med i tidningen'}
        className={`text-[11px] leading-none rounded px-0.5 ${m.in_paper ? '' : 'opacity-30 grayscale'}`} data-testid={`monthly-important-paper-${idx}`}>📰</button>
      {open && (
        <span className="absolute right-0 top-6 z-20 bg-white border-2 border-[#C8B6FF] rounded-lg p-2 shadow-lg flex flex-col gap-1 w-44" data-testid={`monthly-important-date-editor-${idx}`}>
          <span className="text-[9px] text-[#7D6BA0]">Rätta datum och tid</span>
          <input type="date" value={m.date || parsed?.date || ''} onChange={(e) => onMeta({ date: e.target.value })} className="text-[11px] border rounded px-1" data-testid={`monthly-important-date-input-${idx}`} />
          <input type="time" value={m.time || parsed?.time || ''} onChange={(e) => onMeta({ time: e.target.value })} className="text-[11px] border rounded px-1" data-testid={`monthly-important-time-input-${idx}`} />
          {(m.date || m.time) && <button onClick={() => onMeta({ date: '', time: '' })} className="text-[9px] underline text-[#7D6BA0] text-left" data-testid={`monthly-important-date-reset-${idx}`}>Låt appen läsa datumet ur texten</button>}
        </span>
      )}
    </span>
  );
}

export default function AmethystMonthlyPlanner({ year, month, onChangeMonth, onPickDay }) {
  const [data, setData] = useState(null);
  const [grid, setGrid] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [alarmRow, setAlarmRow] = useState(null); // { idx }
  const [dateRow, setDateRow] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, g] = await Promise.all([
        axios.get(`${API}/mysida/monthly-planner?year=${year}&month=${month}`, { withCredentials: true }),
        axios.get(`${API}/mysida/month?year=${year}&month=${month}`, { withCredentials: true }),
      ]);
      setData(p.data);
      setGrid(g.data);
    } catch {}
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (patch) => {
    try {
      const r = await axios.put(`${API}/mysida/monthly-planner/${year}-${String(month).padStart(2,'0')}`, patch, { withCredentials: true });
      if (r.data?.important_parsed) {
        setData((prev) => (prev ? { ...prev, important_parsed: r.data.important_parsed } : prev));
        const g = await axios.get(`${API}/mysida/month?year=${year}&month=${month}`, { withCredentials: true });
        setGrid(g.data);
      }
    } catch {}
  }, [year, month]);
  const dsave = useDebouncedSave(save, 700);

  if (!data || !grid) return <div className="text-center py-16 opacity-60">Laddar…</div>;

  const patchLocal = (partial) => setData((prev) => ({ ...prev, ...partial }));
  const setGoal = (i, v) => { const g = [...data.goals]; g[i] = v; patchLocal({ goals: g }); dsave({ goals: g }); };
  const setTodo = (i, v) => { const t = [...data.todo]; t[i] = { ...t[i], text: v }; patchLocal({ todo: t }); dsave({ todo: t }); };
  const toggleTodo = (i) => { const t = [...data.todo]; t[i] = { ...t[i], done: !t[i].done }; patchLocal({ todo: t }); save({ todo: t }); };
  const setPriority = (i, v) => { const p = [...data.top_priorities]; p[i] = v; patchLocal({ top_priorities: p }); dsave({ top_priorities: p }); };
  const setEvent = (i, v) => { const e = [...data.events]; e[i] = v; patchLocal({ events: e }); dsave({ events: e }); };
  const setImportant = (i, v) => {
    const list = [...(data.important_list || Array(15).fill(''))];
    list[i] = v;
    patchLocal({ important_list: list });
    dsave({ important_list: list });
  };
  const setMeta = (i, patch) => {
    const arr = [...(data.important_meta || Array(15).fill({ in_paper: true, date: '', time: '' }))];
    arr[i] = { ...(arr[i] || { in_paper: true, date: '', time: '' }), ...patch };
    patchLocal({ important_meta: arr });
    save({ important_meta: arr });
  };
  const setImportantAlarm = (i, alarmOrNull) => {
    const arr = [...(data.important_alarms || Array(15).fill(null))];
    while (arr.length < 15) arr.push(null);
    arr[i] = alarmOrNull;
    patchLocal({ important_alarms: arr });
    save({ important_alarms: arr }); // save immediately (not debounced) so scheduler picks it up fast
  };
  const setHabitName = (i, v) => { const h = [...data.habits]; h[i] = { ...h[i], name: v }; patchLocal({ habits: h }); dsave({ habits: h }); };
  const toggleHabitDay = (i, di) => {
    const h = [...data.habits];
    const days = [...(h[i].days || [false]*7)];
    days[di] = !days[di];
    h[i] = { ...h[i], days };
    patchLocal({ habits: h }); save({ habits: h });
  };
  const setNotes = (v) => { patchLocal({ notes: v }); dsave({ notes: v }); };
  const setWork = (i, v) => { const w = [...(data.work || ['','','','','',''])]; w[i] = v; patchLocal({ work: w }); dsave({ work: w }); };
  const setFin = (k, v) => { const f = { ...data.financial, [k]: v }; patchLocal({ financial: f }); dsave({ financial: f }); };
  const setReflection = (k, v) => { const r = { ...data.reflection, [k]: v }; patchLocal({ reflection: r }); dsave({ reflection: r }); };

  const shift = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    onChangeMonth(y, m);
  };

  return (
    <div className="neo-card bg-white p-4 md:p-6" data-testid="amethyst-monthly">
      {/* Header */}
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-start gap-3">
          <span className="text-3xl">🌿</span>
          <div>
            <div className="font-['Fredoka'] text-4xl sm:text-5xl font-bold tracking-wider text-[#4E4370]">MONTHLY</div>
            <div className="font-['Fredoka'] italic text-2xl text-[#8B7BAA] -mt-1">Planner</div>
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center gap-2 justify-end mb-1">
            <div className="bg-[#DED0F0] rounded-full px-3 py-1 text-sm font-bold text-[#4E4370] capitalize" data-testid="monthly-month-label">
              {MONTHS[month-1]} {year}
            </div>
            <button onClick={() => shift(-1)} className="neo-btn p-1.5 bg-white" data-testid="monthly-prev-btn"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => shift(1)} className="neo-btn p-1.5 bg-white" data-testid="monthly-next-btn"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="italic text-xs text-[#7D6BA0]">A goal without a plan is just a wish. <span className="text-[#C8B6FF]">♡</span></div>
        </div>
      </div>
      <div className="h-px bg-[#DED0F0] mb-3" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: Calendar grid (cols 1-2) */}
        <div className="lg:col-span-2">
          <div className="grid grid-cols-7 mb-0.5 text-center text-[10px] tracking-widest font-bold text-[#8B7BAA] uppercase bg-[#E8DCF5] rounded-t-md">
            {['MÅN','TIS','ONS','TOR','FRE','LÖR','SÖN'].map((w) => (<div key={w} className="py-1.5">{w}</div>))}
          </div>
          <div className="border-l border-t border-[#DED0F0]">
            {grid.weeks.map((wk, wi) => (
              <div key={wi} className="grid grid-cols-7">
                {wk.map((c) => (
                  <button
                    key={c.day}
                    onClick={() => c.in_month && setEditingCell(c.day)}
                    className={`relative min-h-[70px] p-1 flex flex-col text-left text-[10px] border-r border-b border-[#DED0F0] transition ${
                      !c.in_month ? 'bg-white opacity-40' :
                      c.is_today ? 'bg-[#DED0F0]' :
                      'bg-white hover:bg-[#F5EEFF]'
                    }`}
                    data-testid={`monthly-cell-${c.day}`}
                  >
                    <div className={`font-bold ${c.is_today ? 'text-[#4E4370]' : 'text-[#8B7BAA]'}`}>
                      {parseInt(c.day.slice(-2),10)}
                    </div>
                    {c.text_preview && (
                      <div className="text-[9px] leading-tight text-[#4E4370] mt-0.5 line-clamp-2">{c.text_preview}</div>
                    )}
                    {c.stickers?.length > 0 && <div className="text-[11px] leading-none mt-0.5">{c.stickers.join('')}</div>}
                    {c.alarm_count > 0 && (
                      <div className="absolute top-0.5 right-0.5 text-[7px] bg-[#7D6BA0] text-white rounded-full px-1">🔔 {c.alarm_count}</div>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: sidebar */}
        <div className="space-y-3">
          {/* Viktiga datum · Besök · Uppgifter — 15-radars lista med alarm per rad */}
          <section data-testid="monthly-important-list">
            <div className="bg-[#E8DCF5] rounded-t-md px-3 py-1 flex items-baseline justify-between">
              <div className="text-[10px] tracking-widest font-black text-[#4E4370]">✧ VIKTIGA DATUM · BESÖK</div>
              <div className="italic text-[9px] text-[#8B7BAA]">extra påminnelser</div>
            </div>
            <div className="border-2 border-[#DED0F0] border-t-0 rounded-b-md bg-white p-2">
              <div className="space-y-1">
                {Array.from({ length: 15 }).map((_, i) => {
                  const alarm = (data.important_alarms || [])[i];
                  const hasAlarm = !!(alarm && alarm.at);
                  return (
                    <div key={i} className="flex items-center gap-1.5" data-testid={`monthly-important-row-${i}`}>
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ background: '#E4DDD1', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)' }}
                      />
                      <input
                        type="text"
                        value={(data.important_list || [])[i] || ''}
                        onChange={(e) => setImportant(i, e.target.value)}
                        placeholder=""
                        className="flex-1 bg-transparent outline-none text-[11px] text-[#4E4370] py-0.5 min-w-0"
                        style={{ borderBottom: '1px dotted #C8BFB0' }}
                        maxLength={160}
                        data-testid={`monthly-important-input-${i}`}
                      />
                      <RowDateChip parsed={(data.important_parsed || [])[i]} meta={(data.important_meta || [])[i]} text={(data.important_list || [])[i]}
                        open={dateRow === i} onOpen={() => setDateRow(dateRow === i ? null : i)} onMeta={(patch) => setMeta(i, patch)} idx={i} />
                      <button
                        onClick={() => setAlarmRow(i)}
                        title={hasAlarm ? `Alarm: ${new Date(alarm.at).toLocaleString('sv-SE',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}${alarm.repeat && alarm.repeat !== 'none' ? ' · upprepas' : ''}` : 'Lägg till alarm'}
                        className={`flex-shrink-0 p-1 rounded-full transition relative ${hasAlarm ? 'bg-[#C8B6FF] text-white' : 'bg-transparent text-[#B4A88E] hover:bg-[#F5EEFF] hover:text-[#7D6BA0]'}`}
                        data-testid={`monthly-important-alarm-btn-${i}`}
                      >
                        {hasAlarm ? <BellRing className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
                        {hasAlarm && alarm.repeat && alarm.repeat !== 'none' && (
                          <span className="absolute -top-1 -right-1 text-[7px] bg-white text-[#7D6BA0] rounded-full w-3 h-3 flex items-center justify-center border border-[#C8B6FF]">🔁</span>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
              {/* beige footer bar */}
              <div className="h-2 mt-2 -mx-2 -mb-2 rounded-b-md" style={{ background: '#E4DDD1' }} />
            </div>
          </section>

          {/* This Month's Goals */}
          <section>
            <div className="bg-[#E8DCF5] rounded-t-md px-3 py-1 text-center text-[10px] tracking-widest font-black text-[#4E4370]">THIS MONTH'S GOALS</div>
            <div className="border-2 border-[#DED0F0] border-t-0 rounded-b-md p-2 space-y-1">
              {[0,1,2,3,4].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="italic text-xs text-[#7D6BA0] w-3">{i+1}.</span>
                  <input value={data.goals?.[i] || ''} onChange={(e) => setGoal(i, e.target.value)} className="flex-1 border-b border-dotted border-[#C8B6FF] outline-none text-xs py-0.5" data-testid={`monthly-goal-${i}`} />
                </div>
              ))}
            </div>
          </section>

          {/* To-Do */}
          <section>
            <div className="bg-[#E8DCF5] rounded-t-md px-3 py-1 text-center text-[10px] tracking-widest font-black text-[#4E4370]">TO-DO LIST</div>
            <div className="border-2 border-[#DED0F0] border-t-0 rounded-b-md p-2 space-y-1">
              {data.todo?.map((t, i) => (
                <div key={i} className="flex items-center gap-2">
                  <button onClick={() => toggleTodo(i)} className={`w-3 h-3 border-2 border-[#8B7BAA] rounded-sm flex-shrink-0 ${t.done ? 'bg-[#8B7BAA]' : ''}`} data-testid={`monthly-todo-check-${i}`} />
                  <input value={t.text || ''} onChange={(e) => setTodo(i, e.target.value)} className={`flex-1 border-b border-dotted border-[#C8B6FF] outline-none text-xs py-0.5 ${t.done ? 'line-through opacity-50' : ''}`} data-testid={`monthly-todo-${i}`} />
                </div>
              ))}
            </div>
          </section>

          {/* Habit Tracker */}
          <section>
            <div className="bg-[#E8DCF5] rounded-t-md px-3 py-1 text-center text-[10px] tracking-widest font-black text-[#4E4370]">HABIT TRACKER</div>
            <div className="border-2 border-[#DED0F0] border-t-0 rounded-b-md p-2">
              <div className="grid gap-0.5" style={{gridTemplateColumns:'1fr repeat(7,20px)'}}>
                <div />
                {HABIT_DAY_LABELS.map((d) => (<div key={d} className="text-[8px] text-center font-bold text-[#8B7BAA]">{d}</div>))}
                {data.habits?.map((h, i) => (
                  <React.Fragment key={i}>
                    <input value={h.name || ''} onChange={(e) => setHabitName(i, e.target.value)} placeholder="—" className="text-[9px] border-b border-dotted border-[#C8B6FF] outline-none py-0.5" data-testid={`monthly-habit-name-${i}`} />
                    {HABIT_DAY_LABELS.map((_, di) => (
                      <button key={di} onClick={() => toggleHabitDay(i, di)} className={`w-4 h-4 rounded-full border-2 border-[#8B7BAA] mx-auto ${h.days?.[di] ? 'bg-[#8B7BAA]' : 'bg-white'}`} data-testid={`monthly-habit-${i}-day-${di}`} />
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </section>

          {/* Notes — spiral-bound beige NOTE-block */}
          <section data-testid="monthly-note-block">
            <div
              className="relative rounded-md px-3 py-3"
              style={{
                background: 'linear-gradient(180deg, #F4EFDF 0%, #EBE0C7 100%)',
                border: '1px solid #D8C9A6',
                boxShadow: 'inset 0 0 0 2px #F4EFDF, 0 2px 8px rgba(150, 120, 60, 0.15)',
              }}
            >
              <div className="absolute top-2 left-1 bottom-2 flex flex-col justify-around">
                {[0,1,2,3,4,5,6,7].map((i) => (
                  <div key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: '#B49A6A' }} />
                ))}
              </div>
              <div className="pl-5">
                <div className="font-['Fredoka'] italic text-lg text-[#6B5A3C] mb-1">NOTE.</div>
                <textarea
                  value={data.notes || ''}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={5}
                  className="w-full bg-transparent outline-none text-sm resize-none leading-relaxed text-[#4E4370]"
                  style={{ backgroundImage: 'repeating-linear-gradient(to bottom, transparent, transparent 21px, #C8B6A0 21px, #C8B6A0 22px)' }}
                  data-testid="monthly-notes"
                />
              </div>
            </div>
          </section>

          {/* Jobb-rader (Work) — persists to monthly-planner.notes-adjacent field */}
          <section data-testid="monthly-work-section">
            <div className="bg-[#E8DCF5] rounded-t-md px-3 py-1 text-center text-[10px] tracking-widest font-black text-[#4E4370]">💼 JOBB</div>
            <div className="border-2 border-[#DED0F0] border-t-0 rounded-b-md p-2 space-y-1">
              {[0,1,2,3,4,5].map((i) => (
                <input
                  key={i}
                  value={(data.work || [])[i] || ''}
                  onChange={(e) => setWork(i, e.target.value)}
                  placeholder={`Jobb ${i+1}`}
                  className="w-full text-sm border-b border-[#DED0F0] py-0.5 outline-none bg-transparent"
                  data-testid={`monthly-work-${i}`}
                />
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        {/* Top Priorities This Month */}
        <section className="bg-[#F5EEFF] rounded-lg p-3">
          <div className="text-[10px] tracking-widest font-black text-[#4E4370] text-center mb-2">TOP PRIORITIES THIS MONTH</div>
          <div className="space-y-1.5">
            {[0,1,2,3,4].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[#C8B6FF]">♥</span>
                <input value={data.top_priorities?.[i] || ''} onChange={(e) => setPriority(i, e.target.value)} className="flex-1 border-b border-dotted border-[#C8B6FF] outline-none text-xs py-0.5" data-testid={`monthly-priority-${i}`} />
              </div>
            ))}
          </div>
        </section>

        {/* Important Dates / Events */}
        <section className="bg-[#F5EEFF] rounded-lg p-3">
          <div className="text-[10px] tracking-widest font-black text-[#4E4370] text-center mb-2">IMPORTANT DATES / EVENTS</div>
          <div className="space-y-1.5">
            {[0,1,2,3,4,5].map((i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-[#8B7BAA]">•</span>
                <input value={data.events?.[i] || ''} onChange={(e) => setEvent(i, e.target.value)} className="flex-1 border-b border-dotted border-[#C8B6FF] outline-none text-xs py-0.5" data-testid={`monthly-event-${i}`} />
              </div>
            ))}
          </div>
        </section>

        {/* Financial Plan */}
        <section className="bg-[#F5EEFF] rounded-lg p-3">
          <div className="text-[10px] tracking-widest font-black text-[#4E4370] text-center mb-2">FINANCIAL PLAN</div>
          <div className="space-y-1">
            {[
              ['income','Månadsinkomst'],['expenses','Utgifter'],['savings','Sparmål'],
              ['debt','Skulder att betala'],['investments','Investeringsmål'],
            ].map(([k, label]) => (
              <div key={k} className="flex items-baseline gap-2 text-xs">
                <span className="w-32 text-[#7D6BA0]">{label}</span>
                <span className="text-[#8B7BAA]">$</span>
                <input value={data.financial?.[k] || ''} onChange={(e) => setFin(k, e.target.value)} className="flex-1 border-b border-dotted border-[#C8B6FF] outline-none py-0.5" data-testid={`monthly-fin-${k}`} />
              </div>
            ))}
          </div>
        </section>

        {/* Monthly Reflection */}
        <section className="bg-[#F5EEFF] rounded-lg p-3">
          <div className="text-[10px] tracking-widest font-black text-[#4E4370] text-center mb-2">MONTHLY REFLECTION</div>
          <div className="space-y-2 text-xs">
            {[
              ['well','Vad gick bra denna månad?'],
              ['challenges','Vilka utmaningar mötte jag?'],
              ['improve','Vad kan jag förbättra nästa månad?'],
              ['grateful','Jag är tacksam för…'],
              ['next_month','Fokus nästa månad'],
            ].map(([k, label]) => (
              <div key={k}>
                <div className="italic text-[#7D6BA0]">{label}</div>
                <textarea value={data.reflection?.[k] || ''} onChange={(e) => setReflection(k, e.target.value)} rows={1} className="w-full border-b border-dotted border-[#C8B6FF] outline-none resize-none py-0.5" data-testid={`monthly-reflect-${k}`} />
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Reminder */}
      <div className="mt-4 text-center italic text-sm text-[#7D6BA0]" data-testid="monthly-reminder">
        <span className="font-['Fredoka'] text-lg">Reminder</span> <span className="text-[#C8B6FF]">♡</span>
        <div className="text-xs mt-1">Small steps every day lead to big changes.</div>
      </div>

      {editingCell && (
        <CellQuickEdit
          date={editingCell}
          onClose={() => setEditingCell(null)}
          onSaved={load}
          onOpenFullDay={() => { const d = editingCell; setEditingCell(null); onPickDay && onPickDay(d); }}
        />
      )}

      {alarmRow !== null && (
        <ImportantAlarmModal
          rowIndex={alarmRow}
          rowText={(data.important_list || [])[alarmRow] || ''}
          alarm={(data.important_alarms || [])[alarmRow] || null}
          year={year}
          month={month}
          onClose={() => setAlarmRow(null)}
          onSave={(a) => { setImportantAlarm(alarmRow, a); setAlarmRow(null); }}
          onRemove={() => { setImportantAlarm(alarmRow, null); setAlarmRow(null); }}
        />
      )}
      <div className="mt-4">
        <ChoreBoard date={todayYmd().startsWith(`${year}-${String(month).padStart(2, '0')}`) ? todayYmd() : `${year}-${String(month).padStart(2, '0')}-01`}
          freqs={['month', 'quarter', 'year']} title="Månadens sysslor" />
      </div>
    </div>
  );
}

// ============== CELL QUICK-EDIT POPOVER (title + up to 3 alarms + work) ==============
function CellQuickEdit({ date, onClose, onSaved, onOpenFullDay }) {
  const [title, setTitle] = useState('');
  const [alarms, setAlarms] = useState([]);
  const [work, setWork] = useState(['','','','','','']);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/mysida/daily-planner?date=${date}`, { withCredentials: true })
      .then((r) => {
        setTitle(r.data.title || '');
        setAlarms(r.data.alarms || []);
        setWork((r.data.work || ['','','','','','']).slice(0,6).concat(['','','','','','']).slice(0,6));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [date]);

  const save = async (partial) => {
    try {
      await axios.put(`${API}/mysida/daily-planner/${date}`, partial, { withCredentials: true });
      onSaved && onSaved();
    } catch {}
  };

  const addAlarm = () => {
    if (alarms.length >= 3) return;
    const now = new Date(); now.setMinutes(now.getMinutes() + 30);
    const at = new Date(`${date}T${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:00`);
    const na = [...alarms, { at: at.toISOString(), message: '', sound: 'corgi' }];
    setAlarms(na); save({ alarms: na });
  };
  const updateAlarm = (i, patch) => {
    const na = alarms.map((a, ii) => ii === i ? { ...a, ...patch } : a);
    setAlarms(na); save({ alarms: na });
  };
  const removeAlarm = (i) => {
    const na = alarms.filter((_, ii) => ii !== i);
    setAlarms(na); save({ alarms: na });
  };
  const updateWork = (i, v) => {
    const nw = [...work]; nw[i] = v; setWork(nw); save({ work: nw });
  };

  const localAt = (iso) => {
    try { const d = new Date(iso); const pad = (n) => String(n).padStart(2,'0'); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; }
    catch { return ''; }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" onClick={onClose} data-testid="cell-edit-modal">
      <div className="neo-card bg-white p-4 w-full max-w-md max-h-[85vh] overflow-auto space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#8B7BAA]">Snabb-anteckning</div>
            <div className="text-lg font-['Fredoka'] font-bold text-[#4E4370]">{date}</div>
          </div>
          <button onClick={onClose} className="text-[#8B7BAA] text-xl font-bold" data-testid="cell-edit-close">×</button>
        </div>

        {loading ? <div className="text-center py-6 opacity-60">Laddar…</div> : (
          <>
            <label className="block">
              <div className="text-[10px] uppercase tracking-widest font-black text-[#7D6BA0] mb-1">Text (syns i månad + vecka)</div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => save({ title })}
                placeholder="Skriv en kort text för dagen…"
                className="w-full border-2 border-[#DED0F0] rounded-md px-2 py-1.5 text-sm"
                maxLength={120}
                data-testid="cell-title-input"
              />
            </label>

            {/* Alarms */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-widest font-black text-[#7D6BA0]">🔔 Alarm ({alarms.length}/3)</div>
                {alarms.length < 3 && (
                  <button onClick={addAlarm} className="text-[10px] neo-btn px-2 py-0.5 bg-[#E8DCF5]" data-testid="cell-add-alarm-btn">+ Lägg till</button>
                )}
              </div>
              <div className="space-y-1.5">
                {alarms.map((a, i) => (
                  <div key={i} className="flex items-center gap-1.5 bg-[#F5EEFF] rounded-md p-1.5" data-testid={`cell-alarm-${i}`}>
                    <input
                      type="datetime-local"
                      value={localAt(a.at)}
                      onChange={(e) => {
                        try { const iso = new Date(e.target.value).toISOString(); updateAlarm(i, { at: iso }); } catch {}
                      }}
                      className="text-[11px] border border-[#DED0F0] rounded px-1 py-0.5 flex-1"
                      data-testid={`cell-alarm-${i}-at`}
                    />
                    <input
                      type="text"
                      value={a.message || ''}
                      onChange={(e) => updateAlarm(i, { message: e.target.value })}
                      onBlur={(e) => updateAlarm(i, { message: e.target.value })}
                      placeholder="text"
                      className="text-[11px] border border-[#DED0F0] rounded px-1 py-0.5 flex-1"
                      data-testid={`cell-alarm-${i}-msg`}
                    />
                    <button onClick={() => removeAlarm(i)} className="text-[#7D6BA0]" title="Ta bort" data-testid={`cell-alarm-${i}-remove`}>×</button>
                  </div>
                ))}
                {alarms.length === 0 && <div className="text-[11px] opacity-50 italic">Inga alarm ännu</div>}
              </div>
            </div>

            {/* Work rows */}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-black text-[#7D6BA0] mb-1">💼 Jobb-rader</div>
              <div className="space-y-1">
                {work.map((w, i) => (
                  <input
                    key={i}
                    type="text"
                    value={w}
                    onChange={(e) => updateWork(i, e.target.value)}
                    placeholder={`Jobb ${i+1}`}
                    className="w-full border-b border-[#DED0F0] text-sm py-0.5 outline-none bg-transparent"
                    data-testid={`cell-work-${i}`}
                  />
                ))}
              </div>
            </div>

            <button onClick={onOpenFullDay} className="w-full neo-btn py-1.5 bg-[#DED0F0] text-xs font-black" data-testid="cell-open-day-btn">
              Öppna hela dagsplaneraren →
            </button>
          </>
        )}
      </div>
    </div>
  );
}


// ============== IMPORTANT-ROW ALARM MODAL ==============
function ImportantAlarmModal({ rowIndex, rowText, alarm, year, month, onClose, onSave, onRemove }) {
  const toLocal = (iso) => {
    if (!iso) return '';
    try { const d = new Date(iso); const pad = (n) => String(n).padStart(2,'0'); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; }
    catch { return ''; }
  };
  const defaultAt = () => {
    const d = new Date(year, month - 1, Math.min(15, new Date(year, month, 0).getDate()), 9, 0, 0);
    const pad = (n) => String(n).padStart(2,'0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T09:00`;
  };
  const [at, setAt] = useState(alarm?.at ? toLocal(alarm.at) : defaultAt());
  const [message, setMessage] = useState(alarm?.message || '');
  const [repeat, setRepeat] = useState(alarm?.repeat || 'none');

  const handleSave = () => {
    if (!at) return;
    try {
      const iso = new Date(at).toISOString();
      onSave({ at: iso, message: message.slice(0, 160), sound: 'corgi', repeat, fired: false });
    } catch {}
  };

  const REPEAT_OPTIONS = [
    { key: 'none',    label: 'En gång',    emoji: '✨' },
    { key: 'daily',   label: 'Varje dag',  emoji: '🌅' },
    { key: 'weekly',  label: 'Varje vecka',emoji: '📆' },
    { key: 'monthly', label: 'Varje månad',emoji: '🌙' },
    { key: 'yearly',  label: 'Varje år',   emoji: '🎂' },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" onClick={onClose} data-testid="monthly-important-alarm-modal">
      <div className="neo-card bg-white p-4 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#8B7BAA]">🔔 Alarm</div>
            <div className="text-base font-['Fredoka'] font-bold text-[#4E4370]">Rad {rowIndex + 1}</div>
            {rowText && <div className="text-xs text-[#7D6BA0] mt-0.5 italic line-clamp-2">"{rowText}"</div>}
          </div>
          <button onClick={onClose} className="text-[#8B7BAA]" data-testid="monthly-important-alarm-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="block">
          <div className="text-[10px] uppercase tracking-widest font-black text-[#7D6BA0] mb-1">När?</div>
          <input
            type="datetime-local"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            className="w-full border-2 border-[#DED0F0] rounded-md px-2 py-1.5 text-sm"
            data-testid="monthly-important-alarm-at"
          />
        </label>

        <label className="block">
          <div className="text-[10px] uppercase tracking-widest font-black text-[#7D6BA0] mb-1">Meddelande (valfritt)</div>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={rowText ? `t.ex. "${rowText.slice(0,40)}"` : 'Kort påminnelse…'}
            className="w-full border-2 border-[#DED0F0] rounded-md px-2 py-1.5 text-sm"
            maxLength={160}
            data-testid="monthly-important-alarm-msg"
          />
        </label>

        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-[#7D6BA0] mb-1">🔁 Upprepning</div>
          <div className="grid grid-cols-5 gap-1" data-testid="monthly-important-alarm-repeat">
            {REPEAT_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setRepeat(opt.key)}
                className={`text-[10px] py-1.5 rounded-md border-2 transition ${
                  repeat === opt.key
                    ? 'bg-[#C8B6FF] border-[#C8B6FF] text-white font-black'
                    : 'bg-white border-[#DED0F0] text-[#7D6BA0] hover:bg-[#F5EEFF]'
                }`}
                data-testid={`monthly-important-alarm-repeat-${opt.key}`}
              >
                <div className="text-sm leading-none">{opt.emoji}</div>
                <div className="mt-0.5">{opt.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-1">
          {alarm && (
            <button
              onClick={onRemove}
              className="flex-1 neo-btn py-1.5 bg-white text-[#8B7BAA] text-xs font-bold"
              data-testid="monthly-important-alarm-remove"
            >
              Ta bort
            </button>
          )}
          <button
            onClick={handleSave}
            className="flex-1 neo-btn py-1.5 bg-[#C8B6FF] text-white text-xs font-black"
            data-testid="monthly-important-alarm-save"
          >
            {alarm ? 'Uppdatera' : 'Spara alarm'}
          </button>
        </div>

        <div className="text-[10px] text-center italic text-[#8B7BAA] pt-1">
          Farmor Corgi skickar en påminnelse när det är dags 🐶💜
        </div>
      </div>
    </div>
  );
}
